import { EdnaWrapper } from '@providers/EdnaProvider';
import { forwardRef } from 'react';
import styled from 'styled-components';
import Icon from '../Icon';
import { ClickableIconProps, ClickableIconWrapperProps } from './types';

const IconWrapper = styled.button<ClickableIconWrapperProps>`
  all: unset;
  padding: ${(props) => props.padding};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 100%;

  // Sets thet animation properties
  transform: perspective(1px) translateZ(0);
  overflow: hidden;
  transition-property: color;
  transition-duration: 0.4s;

  ${(props) =>
    props.disabled &&
    ` 
    opacity: 0.5;
    pointer-events: none;
  `}

  // create a content behind the wrapper to animate, and styles it
  :before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors.grey100}80;
    border-radius: 100px;
    transform: scale(0);
    transition-property: transform;
    transition-duration: 0.4s;
    transition-timing-function: ease-out;
  }

  /* triggers the ::before animation */
  :hover:before,
  :focus:before {
    /* https://stackoverflow.com/questions/49066011/overflow-hidden-with-border-radius-not-working-on-safari */
    transform: scale(2) translateZ(0);
  }

  :active:before {
    background: ${(props) => props.theme.colors.grey300}80;
  }
`;

/**
 *  Component used to render Icons wrapped with a container that can be clicked. The current list of Icons is available via the variable `IconListType`, exported by the package.
 *  For more information, check the Icon Component.
 *
 *  Note: Not all Icons are available at the current time.
 */

const ClickableIcon = forwardRef<HTMLButtonElement, ClickableIconProps>(
  (
    {
      name,
      padding = '8px',
      width = '20px',
      height = '20px',
      className,
      color = 'grey700',
      onClick,
      ariaLabel,
      tabIndex = 0,
      disabled = false,
      type = 'submit',
    }: ClickableIconProps,
    ref
  ) => {
    return (
      <EdnaWrapper>
        <IconWrapper
          padding={padding}
          className={className}
          onClick={onClick}
          aria-label={ariaLabel}
          tabIndex={tabIndex}
          disabled={disabled}
          type={type}
          ref={ref}
        >
          <Icon name={name} width={width} height={height} color={color} />
        </IconWrapper>
      </EdnaWrapper>
    );
  }
);

ClickableIcon.displayName = 'ClickableIcon';

export default ClickableIcon;
